/**
 * SF-4651: prospect demo wizard. Limits mirror the sf-api checks so the wizard refuses bad
 * answers before a call (sf-api re-checks every one):
 *  - area: controllers/admin/demoBuildsController.js validateCreateBody (SF-4648), same bounds
 *    as the brand site search (SF-4643);
 *  - transcripts: POST /admin/demo-builds/:id/transcripts (SF-4649).
 */

/** Permission for every demo build route on sf-api and the Demos menu here. */
export const DEMOS_PERMISSION = 'admin:demos:create';
/**
 * SF-4746: "See all prospect demos". On its own it opens the list (All demos), a build's page and
 * its chat, read only; it never allows a write.
 */
export const DEMOS_READ_ALL_PERMISSION = 'admin:demos:read_all';

/** Refine resource for the Demos menu (App.tsx and the menu test share it). */
export const DEMOS_RESOURCE = {
  name: 'demos',
  list: '/demos',
  create: '/demos/new',
  show: '/demos/:id',
} as const;

export const demoDetailPath = (id: string) => `/demos/${encodeURIComponent(id)}`;
/** SF-4652: the change set review for one build. */
export const demoReviewPath = (id: string) => `/demos/${encodeURIComponent(id)}/review`;

export const MAX_RADIUS_MILES = 25;
export const MAX_SITES = 50;
export const DEFAULT_RADIUS_MILES = 5;
export const DEFAULT_MAX_SITES = 10;
export const MAX_NOTES_LENGTH = 5000;
export const MAX_AREA_LABEL_LENGTH = 200;
/** SF-4847: sf-api refuses inputs.modules.keys longer than 20 (its demo builds validator). */
export const MAX_MODULE_KEYS = 20;

export const MAX_TRANSCRIPT_FILES = 5;
export const MAX_TRANSCRIPT_BYTES = 10 * 1024 * 1024;
export const TRANSCRIPT_EXTENSIONS = ['.docx', '.pdf', '.txt', '.vtt', '.srt', '.md'] as const;

/** DemoBuild statuses (sf-api models/DemoBuild.js). */
export const DEMO_BUILD_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  awaiting_transcripts: { label: 'Waiting for transcripts', color: 'default' },
  drafting: { label: 'Drafting', color: 'processing' },
  ready_for_review: { label: 'Ready for review', color: 'gold' },
  building: { label: 'Building', color: 'processing' },
  done: { label: 'Done', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

/** SF-4653: the run's progress and results page. */
export const demoProgressPath = (id: string) => `${demoDetailPath(id)}/progress`;

/**
 * SF-4653: a build whose run has started: building, done, or failed after approval (a build that
 * failed while drafting was never approved and has no run). These open on the progress page.
 */
export const hasRun = (build: { status: string; approved_at?: string | null }) =>
  build.status === 'building' ||
  build.status === 'done' ||
  (build.status === 'failed' && !!build.approved_at);

/**
 * SF-4746: a build past its review (failed, cancelled, dismissed, building or done). Its plan and
 * chat open from "View plan and chat", read only: nobody can change the plan or approve it now.
 */
export const isPastReview = (status: string) =>
  ['failed', 'cancelled', 'building', 'done'].includes(status);

/** "Started by": the person's name, else their email. */
export const startedBy = (creator: { name: string | null; email: string | null } | null) =>
  creator?.name || creator?.email || '—';
